import { ApolloError, QueryResult, useQuery, useSubscription } from '@apollo/client'
import { Location, navigate, useLocation } from '@reach/router'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { Link } from '../../../../common/src/components/Link'
import Modal from '../../../../common/src/components/Modal'
import { BodyText, Spacer, IntroText } from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import { diffSecs, dateFmt, timeFmt, areOnDifferentDays, diffFmt } from '../../../../common/src/date'
import { formatOptPhoneNumber } from '../../../../common/src/format'
import keyboard, { KeyActionArguments, KeyAction } from '../../../../common/src/keyboard'
import memoize, { MemoizeStore } from '../../../../common/src/memoize'
import { CancelablePromise, makeCancelable } from '../../../../common/src/promise'
import title from '../../../../common/src/title'
import {
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchThreadQuery,
  FetchIsTypingQuery,
  LeasingAttributionSource,
  ConversationType,
  ThreadBotStatus,
  ActionName,
  ActionInput,
  MessageType,
  ContactInput,
  FetchThreadQueryVariables,
  ThreadUpdatesSubscriptionSubscription,
  ThreadUpdatesSubscriptionSubscriptionVariables,
  ContactUpdatesSubscriptionSubscription,
  ContactUpdatesSubscriptionSubscriptionVariables,
  MessageUpdatesSubscriptionSubscription,
  CalUpdatesSubscriptionSubscription,
  CalUpdatesSubscriptionSubscriptionVariables,
  MessageLoadOptionsInput,
  MessageUpdatesSubscriptionSubscriptionVariables,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import { setIsTyping, updateThread, updateContactName, postAction } from '../../graphql/mutations'
import { fetchThreadQuery } from '../../graphql/queries/FetchThreadQuery'
import { isTypingQuery } from '../../graphql/queries/queries'
import {
  threadUpdatesSubscription,
  contactUpdatesSubscription,
  messageUpdatesSubscription,
  calUpdatesSubscription,
} from '../../graphql/subscriptions'
import EditHeader from '../components/EditHeader'
import { getContactText } from '../components/contact'
import { Body, LeasingPage, LeasingPageBody, LeasingPageDetail, ScrollPane } from '../components/containers'
import { TaskTooltipLink, FirmTaskTooltipLink } from '../components/task'
import { getHash, readHashId } from '../route'
import AvailabilitySection from '../tasks/AvailabilitySection'
import ContextSidebar from './ContextSidebar'
import MessageItem from './MessageItem'
import { MessageSeparator } from './MessageSeparator'
import SendMessageBox from './SendMessageBox'
import { ThreadActionItem } from './ThreadActionItem'
import { TourTypesChooser } from './TourTypesChooser'
import UnitTypesChooser from './UnitTypesChooser'

interface ThreadDetailProps {
  user: FetchUserContextQuery['self']
  thread: NonNullable<FetchInboxQuery['thread']> | NonNullable<FetchTasksPageQuery['thread']>
  onArchiveThread: () => void
  maybeClearSelection?: () => void
}

type ThreadDetailImplProps = ThreadDetailProps &
  QueryResult<FetchThreadQuery, FetchThreadQueryVariables> & { currHash: string }

type ThreadOrMessage =
  | FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  | FetchThreadQuery['thread']['recentActions'][0]

interface ThreadDetailImplState {
  data?: FetchThreadQuery
  /** Thread we're viewing. */
  threadId?: number

  /** Selected message ID, if any. */
  selectedMessageId?: number | null

  replyingToMessage?: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]

  /** Whether to show the popup floor-plans information. */
  showPricing: boolean
  showPricingTitle?: string
  showPricingTaskId?: number

  isTyping: string[]

  timeThreadFirstSeen?: Date
  actionCount: number

  staleThread: boolean
}

class ThreadDetailImpl extends React.PureComponent<ThreadDetailImplProps, ThreadDetailImplState> {
  private archiveHandlers: MemoizeStore<React.SyntheticEvent<HTMLElement>> = {}
  private clickHandlers: MemoizeStore<never> = {}
  private clickOuterHandlers: MemoizeStore<never> = {}
  private scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  private shouldPollTypingIndicator = true // While the component is mounted, set to true. Short-circuits request frame loop.
  private lastTypingRefetchMs = new Date().getTime() // Timestamp when typing indicator state was last fetched.
  private typingIndicatorPollMs = 1000 // Poll for new typing indicator status every second.

  private sendMessageBoxRef: React.RefObject<HTMLTextAreaElement>
  private quickactionInputRef: React.RefObject<HTMLInputElement>
  private lastPollTextarea = '' // The value of the SendMessageBox textarea during last notification to server.
  private lastPollTypeahead = '' // The value of the QuickAction typeahead during last notification to server.

  private cancelablePromise: CancelablePromise<any> | undefined

  constructor(props: ThreadDetailImplProps) {
    super(props)
    this.state = {
      selectedMessageId: readHashId('message', props.currHash),
      showPricing: false,
      isTyping: props.data?.thread?.isTyping || [],
      timeThreadFirstSeen: new Date(),
      actionCount: 0,
      staleThread: false,
    }

    this.sendMessageBoxRef = React.createRef()
    this.quickactionInputRef = React.createRef()
  }

  componentDidMount() {
    console.log('ThreadDetail componentDidMount, thread ' + this.props.thread?.id)
    keyboard.addListener(this.onKeyboardBound)
    window.addEventListener('focus', this.onFocus)

    this.shouldPollTypingIndicator = true
    this.typingIndicatorFrame()
  }

  // Users have switched focus to this current tab
  private onFocus = () => {
    console.log('thread ' + this.props.thread?.id + ' is in focus')
    this.setState({ timeThreadFirstSeen: new Date() })
  }

  // Users have switched focus to this current tab
  private setStaleThread = (stale: boolean) => {
    console.log('thread ' + this.props.thread?.id + ' is now stale ' + stale)
    this.setState({ staleThread: stale })
  }

  /**
   * Polls server for new typing indicator status every second.
   *
   * Sends updated typing indicator state.
   *
   * Only runs while the tab is active and the component is mounted.
   */
  typingIndicatorFrame = () => {
    if (!this.shouldPollTypingIndicator) {
      // short-circuit this loop when the component unmounts
      return
    }

    this.maybeRefreshTypingIndicator()

    window.requestAnimationFrame(this.typingIndicatorFrame)
  }

  maybeRefreshTypingIndicator = () => {
    const nowMs = new Date().getTime()
    if (nowMs - this.lastTypingRefetchMs > this.typingIndicatorPollMs) {
      this.refreshTypingIndicator()
      this.lastTypingRefetchMs = nowMs
    }
  }

  refreshTypingIndicator = () => {
    this.cancelablePromise = makeCancelable(
      apolloWebClient.query<FetchIsTypingQuery>({
        query: isTypingQuery,
        variables: { threadId: this.props.thread?.id },
        fetchPolicy: 'network-only',
      })
    )

    this.cancelablePromise.promise
      .then(res => {
        if (res.data?.isTyping) {
          this.setState({ isTyping: res.data.isTyping })
        }
      })
      .catch((e: ApolloError | { canceled: boolean }) => {
        if ('canceled' in e && e.canceled) {
          return
        }
        handleError(e as ApolloError)
      })

    // Set typing indicator if the SendMessage textarea has it's changed
    const currTextarea = this.sendMessageBoxRef.current?.value || ''
    const currTypeahead = this.quickactionInputRef.current?.value || ''
    const hasChanged = this.lastPollTextarea !== currTextarea || this.lastPollTypeahead !== currTypeahead
    const isTyping = currTextarea.length > 0 || currTypeahead.length > 0

    if (hasChanged) {
      setIsTyping({ threadId: this.props.thread?.id, isTyping }).catch(handleError)
    }

    this.lastPollTextarea = currTextarea
    this.lastPollTypeahead = currTypeahead
  }

  componentWillUnmount() {
    if (this.cancelablePromise) {
      this.cancelablePromise.cancel()
    }
    console.log('ThreadDetail componentWillUnmount, thread ' + this.props.thread?.id)
    keyboard.removeListener(this.onKeyboardBound)
    window.removeEventListener('focus', this.onFocus)
    this.shouldPollTypingIndicator = false
  }

  static getDerivedStateFromProps(props: ThreadDetailImplProps, state: ThreadDetailImplState) {
    // always compute selectedMessage from the current location hash
    const selectedMessageId = readHashId('message', props.currHash)
    const data = props.data || state.data

    if (state.threadId != null && state.threadId === props.thread?.id) {
      // Still on the same thread
      return { selectedMessageId, data }
    }
    // New thread
    ThreadDetailImpl.updateTitle(props, state)
    console.log('New thread ' + props.thread?.id)
    return {
      threadId: props.thread?.id,
      replyingToMessage: undefined,
      selectedMessageId,
      data,
      timeThreadFirstSeen: new Date(),
      actionCount: 0,
    }
  }

  /**
   * Returns the id of the most recent outbound, or 0 if thread has no outbounds yet, undefined if thread is not yet loaded.
   */
  static getLastOutboundId(messages?: FetchThreadQuery['thread']['messagesWithLinks']['messages']) {
    if (messages == null) {
      return undefined
    }
    let i = messages.length - 1
    for (; i >= 0; i--) {
      if (!messages[i].isInbound) {
        return messages[i].id
      }
    }
    return 0
  }

  render() {
    const { selectedMessageId, showPricing, isTyping, showPricingTitle, showPricingTaskId, data } = this.state
    const { user, onArchiveThread, maybeClearSelection } = this.props
    if (data == null) {
      return null
    }

    const task = this.props.thread?.leasingTask

    if (task == null) {
      return null
    }

    const { thread } = data
    const { leasingAttributionSource, messagesWithLinks } = thread
    const { messages, messageLinks } = messagesWithLinks
    const botThread = thread.botThread

    // Scroll either to the message they've selected, or the most recent message they have not yet read.
    const scrollToMessageId = this.calcScrollToMessageId(messages, selectedMessageId)

    const isMissingMsgs = messages.length === Fragments.threadMessagesLimit

    return (
      <LeasingPage>
        <LeasingPageBody data-testid={TestIds.threadDetailBody}>
          {showPricing && (
            <Modal
              title={showPricingTitle ? showPricingTitle : task.displayName}
              onClose={this.togglePricing}
              width={720}
            >
              <AvailabilitySection taskId={showPricingTaskId ? showPricingTaskId : task.id} />
            </Modal>
          )}
          <MessagesHeaderWrap>{this.renderMessagesHeader(thread, task)}</MessagesHeaderWrap>
          <ScrollPane ref={this.scrollRef}>
            <Body onClick={this.onMessageClick}>
              <MessageList id="message-list">
                {
                  <PseudoMessage isInbound>
                    <BodyText>
                      Source:{' '}
                      <Link color="silver" onClick={this.editAttributionSourceBound}>
                        {leasingAttributionSource}
                      </Link>
                      <Spacer width={16} />
                      <Link color="silver" href={thread.transcriptUrl}>
                        Transcript
                      </Link>
                    </BodyText>
                    {isMissingMsgs && <BodyText>Showing last {Fragments.threadMessagesLimit} messages only</BodyText>}
                  </PseudoMessage>
                }
                {this.renderMessages(messages, thread, selectedMessageId, messageLinks, user, scrollToMessageId)}
              </MessageList>
              <SendMessageBox
                textareaRef={this.sendMessageBoxRef}
                quickactionRef={this.quickactionInputRef}
                isTyping={isTyping}
                user={user}
                thread={thread}
                botThread={botThread}
                onArchiveThread={onArchiveThread}
                maybeClearSelection={maybeClearSelection}
                task={task}
                replyingToMessage={this.state.replyingToMessage}
                resetReplyTo={this.resetReplyTo}
                timeThreadFirstSeen={this.state.timeThreadFirstSeen}
                staleThread={this.state.staleThread}
                setStaleThread={this.setStaleThread}
                onSubmit={this.onSubmit}
                actionCount={this.state.actionCount}
              />
            </Body>
          </ScrollPane>
        </LeasingPageBody>
        <LeasingPageDetail>
          {task && (
            <InlineErrorBoundary componentName="ContextSidebar">
              <ContextSidebar user={user} task={task} thread={thread} />
            </InlineErrorBoundary>
          )}
        </LeasingPageDetail>
      </LeasingPage>
    )
  }

  private onSubmit = () => {
    this.setState({
      actionCount: this.state.actionCount + 1,
      timeThreadFirstSeen: new Date(),
    })
  }

  /**
   * {@code args} will be provided if this method is fired by {@link LazyLinkifiedText}.
   * No arguments will be provided when triggered by a shortcut key.
   */
  private togglePricing = (args?: KeyActionArguments) => {
    const { showPricingTitle, showPricingTaskId } = args ? args : ({} as KeyActionArguments)
    this.setState({ showPricing: !this.state.showPricing, showPricingTitle, showPricingTaskId })
  }

  private resetReplyTo = () => this.setState({ replyingToMessage: undefined })

  private onReplyTo = (message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]) => {
    this.setState({
      replyingToMessage: message,
    })

    if (this.scrollRef.current) {
      this.scrollRef.current.scrollTop = this.scrollRef.current.scrollHeight
    }
  }

  private editAttributionSourceBound = () => {
    const { thread } = this.props
    const { data } = this.state
    if (!thread?.id || data == null) {
      return
    }
    const opts = Object.keys(LeasingAttributionSource).join(', ')
    const leasingAttributionSource = window.prompt(`Enter new source. Options: \n${opts}`)
    updateThread({ threadId: thread.id, leasingAttributionSource }, 'Source updated').catch(handleError)
  }

  /**
   * Returns the header that appears above messages in a thread.
   */
  private renderMessagesHeader(
    thread: FetchThreadQuery['thread'],
    task:
      | NonNullable<FetchInboxQuery['thread']>['leasingTask']
      | NonNullable<FetchTasksPageQuery['thread']>['leasingTask']
  ) {
    const contact = thread.contactThem
    const isProspect = thread.conversationType === ConversationType.PROSPECT
    const archiveUnarchiveElem = thread.isArchived ? this.renderUnarchiveButton() : this.renderArchiveButton()

    return (
      <MessagesHeader>
        <ContactNameEditor messageType={thread.messageType} contact={contact} onEdit={updateContactName} />
        <ThreadHeaderControls>
          <HeaderIntro>
            <TaskTooltipLink task={task} iconSize="small" />
          </HeaderIntro>
          <IntroText>
            {thread.botStatus === ThreadBotStatus.ACTIVE && (
              <Link color={'snow'} onClick={this.stopBot}>
                Stop bot
              </Link>
            )}
            {thread.botStatus === ThreadBotStatus.STOPPED && (
              <Link color={'snow'} onClick={this.startBot}>
                Start bot
              </Link>
            )}
            {thread.botStatus === ThreadBotStatus.STOPPED && <Spacer width={8} />}
            {thread.botStatus === ThreadBotStatus.STOPPED && (
              <Link color={'coral'} onClick={this.block}>
                Block
              </Link>
            )}
            {thread.botStatus === ThreadBotStatus.FIRM_BLOCKED && (
              <Link color={'coral'} onClick={this.stopBot}>
                Unblock
              </Link>
            )}
            <Spacer width={8} />
            {archiveUnarchiveElem}
          </IntroText>
        </ThreadHeaderControls>
        <ThreadHeaderControls>
          <HeaderIntro>
            <FirmTaskTooltipLink task={task} />
            {task == null && '(No task)'}
          </HeaderIntro>
          <IntroText>
            {isProspect && !thread.isArchived && (
              <>
                <Spacer width={8} />
                {'Follow up in '}
                <Link color="snow" onClick={this.getArchiveHandler(1)}>
                  1d
                </Link>{' '}
              </>
            )}
          </IntroText>
        </ThreadHeaderControls>
        <Spacer height={16} />
        {isProspect && task != null && (
          <ChooserWrap>
            <UnitTypesChooser
              taskId={task.id}
              threadId={thread.id}
              selectedUnitTypes={thread.unitTypesInterested}
              setStaleThread={this.setStaleThread}
            />
            <FloatRight>
              <TourTypesChooser
                threadId={thread.id}
                preferredTourType={thread.preferredTourType}
                tourTypeOptions={task.tourTypes}
                setStaleThread={this.setStaleThread}
              />
            </FloatRight>
          </ChooserWrap>
        )}
      </MessagesHeader>
    )
  }

  private calcScrollToMessageId(
    messages: FetchThreadQuery['thread']['messagesWithLinks']['messages'],
    selectedMessageId: number | null | undefined
  ) {
    let scrollToMessageId = selectedMessageId
    if (scrollToMessageId == null && messages.length > 0) {
      scrollToMessageId = messages[messages.length - 1].id
    }
    return scrollToMessageId || undefined
  }

  /**
   * Returns an ordered list of elements that comprise the chat history. elements can either be the
   * messages themselves, OR they can be the timestamp separators that go between messages.
   */
  private renderMessages(
    messages: FetchThreadQuery['thread']['messagesWithLinks']['messages'],
    thread: FetchThreadQuery['thread'],
    selectedMessageId: number | null | undefined,
    messageLinks: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks'],
    user: FetchUserContextQuery['self'],
    scrollToMessageId?: number
  ): React.ReactNode {
    // max time difference between two messages for messages to not be separated with a timestamp
    const recentMessageCutoffInSeconds = 60 * 60 * 4
    // list of message elements and timestamp separators

    const recentActionsById = thread.recentActions.reduce(
      (acc: any, recentAction: FetchThreadQuery['thread']['recentActions'][0]) => {
        acc[recentAction.id] = recentAction
        return acc
      },
      {}
    )

    const actionsToDisplay = thread.recentActions.filter(
      action =>
        action.actionName != null &&
        [
          ActionName.ARCHIVE,
          ActionName.UNARCHIVE,
          ActionName.SNOOZE_THREAD,
          ActionName.START_BOT,
          ActionName.STOP_BOT,
        ].includes(action.actionName)
    )

    const messagesAndActions: ThreadOrMessage[] = (messages as ThreadOrMessage[])
      .concat(actionsToDisplay)
      .sort((a: ThreadOrMessage, b: ThreadOrMessage) => diffSecs(a.timeCreated, b.timeCreated))

    const result = [
      <ThreadCreatedAtWrap key="0">{`Created on ${dateFmt(thread.timeCreated)} at ${timeFmt(
        thread.timeCreated
      )}`}</ThreadCreatedAtWrap>,
    ]

    for (let i = 0; i < messagesAndActions.length; i++) {
      const messageOrAction = messagesAndActions[i]

      if (i > 0) {
        // we can only place a separator now if there was a message before it
        // and it was > `recentMessageCutoffInSeconds` in the past, or on a
        // different day. these are different conditions and we display separators
        //  differently based on which one it is.
        const secondsDiff = diffSecs(messageOrAction.timeCreated, messagesAndActions[i - 1].timeCreated)
        const diffDays = i > 0 && areOnDifferentDays(messagesAndActions[i - 1].timeCreated, messageOrAction.timeCreated)
        const notRecent = secondsDiff > recentMessageCutoffInSeconds

        if (diffDays || notRecent) {
          result.push(
            <MessageSeparator
              showDate={diffDays}
              showTime={true}
              key={messageOrAction.id + '_separator'}
              dateString={messageOrAction.timeCreated}
            />
          )
        }
      }

      // eslint-disable-next-line
      if (messageOrAction.__typename === 'Message') {
        result.push(
          <MessageItem
            key={i + 1}
            message={messageOrAction}
            messageAction={messageOrAction.actionId == null ? undefined : recentActionsById[messageOrAction.actionId]}
            disableReclassification={true}
            thread={thread}
            isSelected={messageOrAction.id === selectedMessageId}
            shouldScrollIntoView={messageOrAction.id === scrollToMessageId}
            index={i}
            onClick={memoize(
              this.clickHandlers,
              messageOrAction.id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              (id: number | null) => (e: React.MouseEvent<HTMLElement>) => this.onMessageClick(e as any, id)
            )}
            onClickOuter={memoize(this.clickOuterHandlers, messageOrAction.id, this.makeClickOuterHandler)}
            onReplyTo={this.onReplyTo}
            messageLinks={messageLinks}
            user={user}
          />
        )
      } else {
        result.push(
          <ThreadActionItem
            key={i + 1}
            recentAction={messageOrAction as FetchThreadQuery['thread']['recentActions'][0]}
          />
        )
      }
    }

    return result
  }

  private static updateTitle(props: ThreadDetailImplProps, state: ThreadDetailImplState) {
    const task = props.thread?.leasingTask
    const thread = state.data ? state.data.thread : null
    let newTitle = task == null ? '(No task)' : task.displayName
    if (thread != null && thread.contactThem != null) {
      newTitle = getContactText(thread.contactThem) + ' - ' + newTitle
    }
    title.setMaybeFlashing(newTitle)
  }

  private updateThread(
    thread: FetchThreadQuery['thread'],
    botStatus?: ThreadBotStatus,
    archiveThread?: boolean,
    unarchiveThread?: boolean,
    archiveDays?: number,
    actionName?: ActionName
  ) {
    const { onArchiveThread, maybeClearSelection } = this.props
    const variables = {
      botStatus,
      archiveThread,
      unarchiveThread,
      archiveDays,
      commandType: 'UPDATE_THREAD',
      threadId: thread.id,
    }

    const operatorResponseTimeMs = this.state.timeThreadFirstSeen
      ? new Date().getTime() - this.state.timeThreadFirstSeen.getTime()
      : null

    const action: ActionInput = {
      actionName: actionName != null ? actionName : ActionName.UPDATE_THREAD,
      commandJsons: [JSON.stringify(variables)],
      logSummary: JSON.stringify({ operatorResponseTimeMs }),
    }

    // optimistic archive
    if (archiveThread || archiveDays != null) {
      onArchiveThread()
    }

    this.onSubmit()
    postAction({ threadId: thread.id, action, actionCount: this.state.actionCount })
      .then(() => {
        if (archiveThread && maybeClearSelection) {
          maybeClearSelection()
        }
      })
      .catch(handleError)
  }

  private stopBot = () => {
    const { thread } = this.state.data!
    this.updateThread(thread, ThreadBotStatus.STOPPED, undefined, undefined, undefined, ActionName.STOP_BOT)
  }

  private startBot = () => {
    const { thread } = this.state.data!
    this.updateThread(thread, ThreadBotStatus.ACTIVE, undefined, undefined, undefined, ActionName.START_BOT)
  }

  private block = () => {
    const { thread } = this.state.data!
    this.updateThread(thread, ThreadBotStatus.FIRM_BLOCKED)
  }

  private onKeyboardBound = (action: KeyAction, args?: KeyActionArguments) => {
    if (this.state.data == null || this.state.data.thread == null) {
      return
    }
    const { thread } = this.state.data

    console.log('ThreadDetail handling shortcut: %s, threaad %s', action, thread.id)
    if (action === KeyAction.esc) {
      // Unselect selected message
      navigate('#', { replace: true }).catch(handleError)
    } else if (action === KeyAction.enter) {
      if (document.activeElement && document.activeElement instanceof HTMLButtonElement) {
        // Tab + enter to focus a button, then click the button, for example Send SMS
        const activeElem = document.activeElement as HTMLElement
        activeElem.click()
      } else {
        // Nothing focused? Hit enter to go to next message.
        // this.nextMessage()
      }
    } else if (action === KeyAction.togglePricing) {
      this.togglePricing(args)
    } else {
      this.handleKeyboardArchive(thread, action)
    }
  }

  private handleKeyboardArchive(thread: FetchThreadQuery['thread'], action: KeyAction) {
    const name = getContactText(thread.contactThem)
    if (action === KeyAction.toggleArchive) {
      if (thread != null && thread.isArchived) {
        this.unarchiveBound()
      } else if (thread != null && !thread.isArchived && window.confirm('Archive ' + name + '?')) {
        this.archive()
      }
    } else if (action === KeyAction.archive1 && window.confirm('Snooze ' + name + ' for 1d?')) {
      this.archive(1)
    }
  }

  private getArchiveHandler = (archiveDays?: number) => {
    const key = archiveDays ? '' + archiveDays : ''
    if (this.archiveHandlers[key] == null) {
      this.archiveHandlers[key] = () => this.archive(archiveDays)
    }
    return this.archiveHandlers[key]
  }

  private archive(archiveDays?: number) {
    if (this.state.data == null || this.state.data.thread == null) {
      return
    }
    const { thread } = this.state.data
    if (thread.isArchived) {
      return
    }

    console.log('ThreadDetail archiving %s snooze: %s', thread.id, archiveDays || 'no snooze')

    const actionName = archiveDays == null ? ActionName.ARCHIVE : ActionName.SNOOZE_THREAD

    this.updateThread(thread, undefined, true, false, archiveDays, actionName)
  }

  private unarchiveBound = () => {
    const { thread } = this.state.data!
    if (!thread.isArchived) {
      return
    }

    this.updateThread(thread, undefined, false, true, undefined, ActionName.UNARCHIVE)
  }

  private renderArchiveButton() {
    return (
      <span>
        <Link color="snow" onClick={this.getArchiveHandler()}>
          Archive
        </Link>
      </span>
    )
  }

  private renderUnarchiveButton() {
    if (this.state.data == null) {
      return null
    }
    const { thread } = this.state.data
    let clockElem = null
    if (thread.archiveUntil != null) {
      const timeStr = diffFmt(thread.archiveUntil, new Date())
      clockElem = <span title={`${timeStr} remaining`}>⏳</span>
    }
    return (
      <Link color="snow" onClick={this.unarchiveBound}>
        Unarchive {clockElem}
      </Link>
    )
  }

  private onMessageClick = (e: React.MouseEvent<HTMLElement>, messageId?: number | null) => {
    const highlightedText = window.getSelection()?.toString()

    // Select this message when the user clicks in the bubble.
    // Do not set if just highlighting contents of a message.
    if (!highlightedText) {
      this.selectMessage(messageId)
    }

    e.stopPropagation()
  }

  private selectMessage(messageId?: number | null) {
    navigate(messageId ? getHash('message', messageId) : '#', { replace: true }).catch(handleError)
  }

  private onClickOuterBound = (e: MouseEvent, messageId: number | null) => {
    if (this.state.selectedMessageId === messageId) {
      // Don't deselect this message if the user clicks anywhere
      // in the details expansion surrounding the selected message
      e.stopPropagation()
    }
  }

  private readonly makeClickOuterHandler = (id: number | null) => (e: React.MouseEvent) => {
    if (this.state.selectedMessageId === id) {
      e.stopPropagation()
    }
    this.onClickOuterBound(e.nativeEvent, id)
  }
}

class ContactNameEditor extends React.PureComponent<{
  messageType: MessageType
  contact: FetchThreadQuery['thread']['contactThem']
  icon?: JSX.Element
  onEdit: (input: ContactInput) => Promise<any>
}> {
  render() {
    const { messageType, contact, icon } = this.props
    const name = [contact.firstName, contact.lastName].filter(s => !!s).join(' ')

    let subheader = null
    if (name !== '') {
      subheader = messageType === 'SMS' ? formatOptPhoneNumber(contact.addressPhone) : contact.addressEmail
    }

    const header = getContactText(contact)

    return (
      <EditHeader
        icon={icon}
        header={header}
        subheader={subheader || undefined}
        editText={name}
        onEdit={this.onEditBound}
      />
    )
  }

  private onEditBound = (fullName: string) => {
    const { onEdit, contact } = this.props
    const names = fullName.match(/\S+/g)
    const firstName = names ? names[0] : null
    const lastName = names && names.length > 1 ? names.slice(1).join(' ') : null

    return onEdit({
      id: contact.id,
      firstName,
      lastName,
    })
  }
}

const MessagesHeaderWrap = styled.div`
  border-bottom: 1px solid #181818;
`

const MessagesHeader = styled.div`
  padding: 8px 32px 0px 48px;
  box-sizing: border-box;
  width: 579px;
`

const ThreadHeaderControls = styled.div`
  display: flex;
  justify-content: space-between;
`

export const MessageList = styled.div`
  margin-top: 48px;
`

export const PseudoMessage = styled.div`
  ${(props: { isInbound?: boolean }) => css`
    margin-bottom: 32px;
    justify-content: ${props.isInbound ? 'flex-start' : 'flex-end'};

    background-color: ${props.isInbound ? Colors.steel : Colors.charcoal};
    border-radius: 4px;
    max-width: 360px;
    box-sizing: border-box;
    padding: 16px 16px 12px 16px;
    position: relative;
  `};
`

const ThreadCreatedAtWrap = styled(BodyText)`
  width: 100%;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 32px;
`

const HeaderIntro = styled(IntroText)`
  flex: 1;
`

export default function ThreadDetail(props: ThreadDetailProps) {
  const { thread } = props
  const contactId = thread.contactThem.id
  const threadId = thread.id

  const selectedMessageId = readHashId('message', useLocation().hash)
  let loadOptions: MessageLoadOptionsInput = { limit: 50 }
  if (selectedMessageId) {
    loadOptions = {
      ...loadOptions,
      messageId: selectedMessageId,
      messagesBefore: 40,
    }
  }

  const res = useQuery<FetchThreadQuery, FetchThreadQueryVariables>(fetchThreadQuery, {
    variables: { threadId: thread.id, loadOptions },
    fetchPolicy: 'cache-and-network',
  })

  const threadRes = useSubscription<
    ThreadUpdatesSubscriptionSubscription,
    ThreadUpdatesSubscriptionSubscriptionVariables
  >(threadUpdatesSubscription, {
    variables: { threadId },
  })

  const contactRes = useSubscription<
    ContactUpdatesSubscriptionSubscription,
    ContactUpdatesSubscriptionSubscriptionVariables
  >(contactUpdatesSubscription, {
    variables: { contactId },
  })

  const messageRes = useSubscription<
    MessageUpdatesSubscriptionSubscription,
    MessageUpdatesSubscriptionSubscriptionVariables
  >(messageUpdatesSubscription, {
    variables: { threadId },
  })

  const calRes = useSubscription<CalUpdatesSubscriptionSubscription, CalUpdatesSubscriptionSubscriptionVariables>(
    calUpdatesSubscription,
    {
      variables: { threadId },
    }
  )

  React.useEffect(() => {
    const { data: threadData } = threadRes
    const { data: contactData } = contactRes
    const { data: messageData } = messageRes
    const { data: calData } = calRes

    if (
      threadData?.threadUpdates?.length ||
      contactData?.contactUpdates?.length ||
      messageData?.messageUpdates?.length ||
      calData?.calUpdates?.length
    ) {
      console.log('[thread] refetching...')
      res.refetch().catch(handleError)
    }
  }, [threadRes.data, contactRes.data, messageRes.data, calRes.data])

  return <Location>{({ location }) => <ThreadDetailImpl {...props} {...res} currHash={location.hash} />}</Location>
}

const ChooserWrap = styled.div`
  height: 38px;
`

const FloatRight = styled.div`
  float: right;
`
